import { ExportInterfaceConfigurationMode, FilterCondition } from 'src/app/base/models/models.swagger.generated';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { Injectable } from '@angular/core';
import { ShowInactiveService } from '../../../base/shared/grid-base/show-inactive.service';
import {
  CreateExportInterfaceConfigTemplateDTO,
  ExportInterfaceConfigTemplateClient,
  ExportInterfaceConfigTemplateDetailsDTO,
  ExportInterfaceConfigurationType,
  FilterDTO,
  FilterOperator,
  SortDirection,
  UpdateExportInterfaceConfigTemplateDTO,
} from '../../../base/models/models.swagger.generated';
import { IAdaptorService } from '../../../base/shared/syncfusion/adaptor-service.interface';
import { Observable, throwError } from 'rxjs';
import { IPagedItems } from '../../../base/shared/syncfusion/paged-items.interface';
import { ToastService } from '../../../base/services/toast.service';
import { catchError, finalize, tap } from 'rxjs/operators';
import { ErrorType } from '../../../core/routing/error-types';

@Injectable()
export class ConfigurationTemplatesService extends ShowInactiveService implements IAdaptorService {

  public oneTimeFilterTemplateId: number = null;
  public configurationType: ExportInterfaceConfigurationType;
  public mode: ExportInterfaceConfigurationMode;
  public doIgnoreTypeFilter = false;

  constructor(
    private exportInterfaceConfigurationTemplateClient: ExportInterfaceConfigTemplateClient,
    private toastService: ToastService,
    private spinnerService: SpinnerService) {
    super();
  }

  public addRecord(): Observable<ExportInterfaceConfigTemplateDetailsDTO> {
    let createConfigurationTemplate = new CreateExportInterfaceConfigTemplateDTO();
    this.spinnerService.show();
    return this.exportInterfaceConfigurationTemplateClient.createExportInterfaceConfigTemplate(createConfigurationTemplate)
      .pipe(
        tap(() => {
          this.toastService.showSuccess(
            'i18n.domain.export-configuration.templates.messages.create.success-title',
            'i18n.domain.export-configuration.templates.messages.create.success-message',
          );
        }),
        finalize(() => this.spinnerService.hide()),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError, error.response);
          }
          return throwError(() => error);
        }),
      );
  }

  public deleteRecord(id: number): Observable<any> {
    this.spinnerService.show();
    return this.exportInterfaceConfigurationTemplateClient.deleteExportInterfaceConfigTemplate(id)
      .pipe(
        tap(() => {
          this.toastService.showSuccess(
            'i18n.domain.export-configuration.templates.messages.delete.success-title',
            'i18n.domain.export-configuration.templates.messages.delete.success-message',
          );
        }),
        finalize(() => this.spinnerService.hide()),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        }),
      );
  }

  public getData(skip: number, take: number, filters: FilterDTO[], sortBy: string, sortDirection: SortDirection): Observable<IPagedItems> {
    if (filters == null) {
      filters = [];
    }
    if (this.configurationType != null && !this.doIgnoreTypeFilter) {
      filters.push(new FilterDTO({
        isComplex: false,
        field: "Type",
        operator: FilterOperator.Equal,
        value: this.configurationType.toString()
      }));
    }
    if (this.oneTimeFilterTemplateId) {
      filters.push(new FilterDTO({ field: 'id', operator: FilterOperator.Equal, value: this.oneTimeFilterTemplateId.toString(), isComplex: false}));
      this.oneTimeFilterTemplateId = null;
    }
    if (this.mode != null) {
      filters.push(new FilterDTO({ field: 'Mode', operator: FilterOperator.Equal, value: this.mode.toString(), isComplex: false }));
      if (this.mode === ExportInterfaceConfigurationMode.Response) {
        filters.push( new FilterDTO({
          isComplex: true,
          condition: FilterCondition.And,
          predicates: [
            new FilterDTO({ field: 'TransferType', operator: FilterOperator.NotEqual, value: '3', isComplex: false }), // REST
            new FilterDTO({ field: 'TransferType', operator: FilterOperator.NotEqual, value: '4', isComplex: false }), // SFTPServerOnly
            new FilterDTO({ field: 'TransferType', operator: FilterOperator.NotEqual, value: '5', isComplex: false }), // AS2
          ]
        }));
      }
    }
    return this.exportInterfaceConfigurationTemplateClient.searchExportInterfaceConfigTemplates(skip, take, sortBy, sortDirection, false, filters);
  }

  public updateRecord(updateConfiguration: UpdateExportInterfaceConfigTemplateDTO): Observable<any> {
    this.spinnerService.show();
    return this.exportInterfaceConfigurationTemplateClient.updateExportInterfaceConfigTemplate(updateConfiguration)
      .pipe(
        finalize(() => this.spinnerService.hide()),
        tap(() => {
          this.toastService.showSuccess(
            'i18n.domain.export-configuration.configurations.messages.update.success-title',
            'i18n.domain.export-configuration.configurations.messages.update.success-message',
          );
        }),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError, error.response);
          }
          return throwError(() => error);
        }),
      );
  }

  public getConfigurationDetails(id: number): Observable<ExportInterfaceConfigTemplateDetailsDTO> {
    return this.exportInterfaceConfigurationTemplateClient.getExportInterfaceConfigTemplateDetails(id);
  }

}
